import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Bot, 
  GraduationCap, 
  Calculator, 
  ShieldAlert, 
  HelpCircle,
  TrendingUp,
  TrendingDown,
  ChevronRight,
  Crown,
  PieChart,
  BarChart3,
  Wallet,
  Plus,
  ArrowUpRight,
  ArrowDownRight,
  Pencil,
  Trash2,
  Loader2,
  Bell,
  Info
} from 'lucide-react';
import Layout from '../components/Layout';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Area, AreaChart } from 'recharts';
import { toast } from 'sonner';
import OnboardingWizard, { isOnboardingCompleted } from '../components/OnboardingWizard';
import PortfolioPerformanceChart from '../components/PortfolioPerformanceChart';
import { InvestmentDisclaimer } from '../components/DisclaimerComponents';
import { InstallAppCard, FloatingInstallButton } from '../components/InstallApp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [stocks, setStocks] = useState([]);
  const [uttFunds, setUttFunds] = useState([]);
  const [etfs, setEtfs] = useState([]);
  const [bonds, setBonds] = useState([]);
  const [marketIndex, setMarketIndex] = useState(null);
  const [indexHistory, setIndexHistory] = useState([]);
  const [marketDataStatus, setMarketDataStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [marketSubTab, setMarketSubTab] = useState('stocks');
  
  // Edit/Delete states
  const [editingItem, setEditingItem] = useState(null);
  const [editForm, setEditForm] = useState({ quantity: '', purchase_price: '', notes: '' });
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(!isOnboardingCompleted());

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, portfolioRes, stocksRes, indexRes, indexHistoryRes, marketStatusRes, assetsRes] = await Promise.all([
        axios.get(`${API}/dashboard/stats`),
        axios.get(`${API}/portfolio`),
        axios.get(`${API}/stocks`),
        axios.get(`${API}/market/index`),
        axios.get(`${API}/market/index/DSEI/history?period=1M`),
        axios.get(`${API}/market-data/status`).catch(() => ({ data: null })),
        axios.get(`${API}/investable-assets`)
      ]);
      setStats(statsRes.data);
      setPortfolio(portfolioRes.data);
      setStocks(stocksRes.data.stocks);
      setMarketIndex(indexRes.data.indices);
      setIndexHistory(indexHistoryRes.data.history);
      setMarketDataStatus(marketStatusRes.data);
      
      // Set UTT, ETFs, and Bonds from investable-assets
      if (assetsRes.data?.assets) {
        setUttFunds(assetsRes.data.assets.filter(a => a.type === 'utt'));
        setEtfs(assetsRes.data.assets.filter(a => a.type === 'etf'));
        setBonds(assetsRes.data.assets.filter(a => a.type === 'bond'));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const formatCurrency = (amount) => {
    if (amount >= 1000000000000) {
      return `${(amount / 1000000000000).toFixed(2)}T`;
    } else if (amount >= 1000000000) {
      return `${(amount / 1000000000).toFixed(2)}B`;
    } else if (amount >= 1000000) {
      return `${(amount / 1000000).toFixed(2)}M`;
    } else if (amount >= 1000) {
      return `${(amount / 1000).toFixed(1)}K`;
    }
    return amount.toLocaleString();
  };

  const quickActions = [
    { icon: Bot, title_en: "AI Coach", title_sw: "Mshauri AI", path: "/chat", color: "bg-primary" },
    { icon: Calculator, title_en: "Simulator", title_sw: "Kihesabu", path: "/simulator", color: "bg-accent" },
    { icon: Bell, title_en: "Price Alerts", title_sw: "Tahadhari Bei", path: "/alerts", color: "bg-orange-500" },
    { icon: GraduationCap, title_en: "Learn", title_sw: "Jifunze", path: "/learn", color: "bg-blue-600" }
  ];

  const getRiskLevelColor = (level) => {
    const colors = {
      'Conservative': 'text-green-600',
      'Moderate-Conservative': 'text-blue-600',
      'Moderate': 'text-yellow-600',
      'Moderate-Aggressive': 'text-orange-600',
      'Aggressive': 'text-red-600'
    };
    return colors[level] || 'text-muted-foreground';
  };

  // Edit portfolio item handlers
  const handleEditClick = (e, item) => {
    e.stopPropagation();
    setEditingItem(item);
    setEditForm({
      quantity: item.quantity.toString(),
      purchase_price: item.purchase_price.toString(),
      notes: item.notes || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!editingItem) return;
    
    setIsSubmitting(true);
    try {
      await axios.put(`${API}/portfolio/${editingItem.id}`, {
        quantity: parseFloat(editForm.quantity),
        purchase_price: parseFloat(editForm.purchase_price),
        notes: editForm.notes || null
      });
      toast.success(t('Investment updated successfully!', 'Uwekezaji umesasishwa!'));
      setIsEditDialogOpen(false);
      setEditingItem(null);
      fetchData(); // Refresh portfolio data
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to update investment', 'Imeshindwa kusasisha uwekezaji'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete portfolio item handlers
  const handleDeleteClick = (e, item) => {
    e.stopPropagation();
    setDeletingItem(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingItem) return;
    
    setIsSubmitting(true);
    try {
      await axios.delete(`${API}/portfolio/${deletingItem.id}`);
      toast.success(t('Investment removed successfully!', 'Uwekezaji umeondolewa!'));
      setIsDeleteDialogOpen(false);
      setDeletingItem(null);
      fetchData(); // Refresh portfolio data
    } catch (error) {
      toast.error(error.response?.data?.detail || t('Failed to remove investment', 'Imeshindwa kuondoa uwekezaji'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      {/* Onboarding Wizard for new users */}
      <OnboardingWizard 
        isOpen={showOnboarding} 
        onClose={() => setShowOnboarding(false)}
        portfolioCount={portfolio?.items?.length || 0}
      />
      
      <div className="page-enter space-y-6">
        {/* Welcome Header */}
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-1">
              {t(`Karibu, ${user?.name?.split(' ')[0]}!`, `Welcome, ${user?.name?.split(' ')[0]}!`)}
            </h1>
            <p className="text-muted-foreground">
              {t("Your investment dashboard", "Dashibodi yako ya uwekezaji")}
            </p>
          </div>
          {stats?.subscription !== 'free' && (
            <div className="flex items-center gap-1 px-3 py-1 bg-accent/10 text-accent rounded-full text-sm font-medium">
              <Crown className="w-4 h-4" />
              {stats?.subscription}
            </div>
          )}
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 lg:w-auto lg:inline-flex">
            <TabsTrigger value="overview" data-testid="tab-overview">
              {t('Overview', 'Muhtasari')}
            </TabsTrigger>
            <TabsTrigger value="portfolio" data-testid="tab-portfolio">
              {t('Portfolio', 'Portfolio')}
            </TabsTrigger>
            <TabsTrigger value="market" data-testid="tab-market">
              {t('Market', 'Soko')}
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6 mt-6">
            {/* Install App Card - Prominent placement for mobile users */}
            <InstallAppCard language={language} />
            
            {/* Portfolio Summary + Market Index */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Portfolio Summary Card */}
              <Card className="border-border/50 overflow-hidden">
                <CardHeader className="pb-2 bg-gradient-to-br from-primary/5 to-accent/5">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-primary" />
                    {t('Your Portfolio', 'Portfolio Yako')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  {portfolio?.summary?.holdings_count > 0 ? (
                    <>
                      <div className="mb-4">
                        <p className="text-sm text-muted-foreground">
                          {t('Total Value', 'Thamani Jumla')}
                        </p>
                        <p className="text-3xl font-bold text-foreground" data-testid="portfolio-value">
                          TZS {formatCurrency(portfolio.summary.total_current_value)}
                        </p>
                        <div className={`flex items-center gap-1 text-sm ${
                          portfolio.summary.total_gain_loss >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {portfolio.summary.total_gain_loss >= 0 ? (
                            <ArrowUpRight className="w-4 h-4" />
                          ) : (
                            <ArrowDownRight className="w-4 h-4" />
                          )}
                          {portfolio.summary.total_gain_loss >= 0 ? '+' : ''}
                          TZS {formatCurrency(Math.abs(portfolio.summary.total_gain_loss))}
                          ({portfolio.summary.total_gain_loss_percent.toFixed(2)}%)
                        </div>
                      </div>
                      <Button 
                        variant="outline" 
                        className="w-full rounded-full"
                        onClick={() => setActiveTab('portfolio')}
                      >
                        {t('View Portfolio', 'Tazama Portfolio')}
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <Wallet className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                      <p className="text-muted-foreground text-sm mb-4">
                        {t('No investments yet', 'Bado hakuna uwekezaji')}
                      </p>
                      <Button 
                        onClick={() => navigate('/portfolio/add')}
                        className="rounded-full"
                        data-testid="add-investment-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('Add Investment', 'Ongeza Uwekezaji')}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Market Index Card */}
              <Card className="border-border/50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <BarChart3 className="w-5 h-5 text-primary" />
                      DSE All Share Index
                    </span>
                    {marketIndex?.DSEI && (
                      <span className={`text-sm font-medium ${
                        marketIndex.DSEI.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {marketIndex.DSEI.ytd_change >= 0 ? '+' : ''}
                        {marketIndex.DSEI.ytd_change.toFixed(2)}% YTD
                      </span>
                    )}
                  </CardTitle>
                  {/* Data Source Indicator */}
                  {marketDataStatus?.initialized && (
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                      <div className={`w-2 h-2 rounded-full ${
                        marketDataStatus.source_type === 'live' ? 'bg-emerald-500 animate-pulse' :
                        marketDataStatus.freshness === 'fresh' ? 'bg-emerald-500' :
                        marketDataStatus.freshness === 'stale' ? 'bg-amber-500' : 'bg-red-500'
                      }`} />
                      <span>
                        {language === 'sw' ? marketDataStatus.source_label_sw : marketDataStatus.source_label}
                        {marketDataStatus.source_type === 'live' && (
                          <span className="ml-1 text-emerald-600 font-medium">● LIVE</span>
                        )}
                        {' • '}{t('Updated', 'Imesasishwa')} {
                          marketDataStatus.last_updated ? 
                            new Date(marketDataStatus.last_updated).toLocaleDateString() : 
                            t('N/A', 'Haijulikani')
                        }
                      </span>
                    </div>
                  )}
                </CardHeader>
                <CardContent className="p-4">
                  {marketIndex?.DSEI && (
                    <p className="text-2xl font-bold text-foreground mb-2">
                      {marketIndex.DSEI.current_value.toLocaleString()}
                    </p>
                  )}
                  <div className="h-32">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={indexHistory.slice(-30)}>
                        <defs>
                          <linearGradient id="colorIndex" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <Area 
                          type="monotone" 
                          dataKey="close" 
                          stroke="hsl(var(--primary))" 
                          strokeWidth={2}
                          fill="url(#colorIndex)" 
                        />
                        <Tooltip 
                          contentStyle={{ 
                            background: 'hsl(var(--card))', 
                            border: '1px solid hsl(var(--border))',
                            borderRadius: '8px'
                          }}
                          formatter={(value) => [`${value.toLocaleString()}`, 'Index']}
                          labelFormatter={(label) => label}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <div>
              <h2 className="text-lg font-semibold text-foreground mb-3">
                {t('Quick Actions', 'Vitendo vya Haraka')}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {quickActions.map((action, i) => (
                  <Card 
                    key={i}
                    className="card-hover cursor-pointer border-border/50"
                    onClick={() => navigate(action.path)}
                    data-testid={`quick-action-${action.path.replace('/', '')}`}
                  >
                    <CardContent className="p-4 text-center">
                      <div className={`w-12 h-12 rounded-xl ${action.color} flex items-center justify-center mx-auto mb-2`}>
                        <action.icon className="w-6 h-6 text-white" />
                      </div>
                      <p className="font-medium text-sm text-foreground">
                        {t(action.title_en, action.title_sw)}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Top Movers */}
            <Card className="border-border/50">
              <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  {t('Top Performers (YTD)', 'Wanaofanya Vizuri (YTD)')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {stocks
                    .sort((a, b) => b.ytd_change - a.ytd_change)
                    .slice(0, 6)
                    .map((stock, i) => (
                      <div 
                        key={stock.symbol}
                        className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                        onClick={() => navigate(`/stocks/${stock.symbol}`)}
                        data-testid={`stock-item-${stock.symbol}`}
                      >
                        <div>
                          <p className="font-semibold text-foreground">{stock.symbol}</p>
                          <p className="text-xs text-muted-foreground truncate max-w-[120px]">
                            {stock.name}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="font-medium text-foreground">
                            {stock.current_price.toLocaleString()}
                          </p>
                          <p className={`text-xs font-medium ${
                            stock.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {stock.ytd_change >= 0 ? '+' : ''}{stock.ytd_change.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>

            {/* Learning Progress */}
            <Card className="border-border/50">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <GraduationCap className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">
                        {t('Learning Progress', 'Maendeleo ya Kujifunza')}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {stats?.learning?.completed || 0} / {stats?.learning?.total || 4} {t('modules', 'moduli')}
                      </p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-primary">
                    {stats?.learning?.percentage || 0}%
                  </span>
                </div>
                <Progress value={stats?.learning?.percentage || 0} className="h-2" />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Portfolio Tab */}
          <TabsContent value="portfolio" className="space-y-6 mt-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">
                {t('My Investments', 'Uwekezaji Wangu')}
              </h2>
              <Button 
                onClick={() => navigate('/portfolio/add')}
                className="rounded-full"
                data-testid="add-investment-btn-2"
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('Add', 'Ongeza')}
              </Button>
            </div>

            {portfolio?.items?.length > 0 ? (
              <>
                {/* Portfolio Summary */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">
                        {t('Total Invested', 'Jumla Iliyowekezwa')}
                      </p>
                      <p className="text-xl font-bold text-foreground">
                        TZS {formatCurrency(portfolio.summary.total_invested)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="bg-gradient-to-br from-accent/10 to-accent/5 border-accent/20">
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">
                        {t('Current Value', 'Thamani ya Sasa')}
                      </p>
                      <p className="text-xl font-bold text-foreground">
                        TZS {formatCurrency(portfolio.summary.total_current_value)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className={`border-${portfolio.summary.total_gain_loss >= 0 ? 'green' : 'red'}-200`}>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">
                        {t('Total Gain/Loss', 'Faida/Hasara Jumla')}
                      </p>
                      <p className={`text-xl font-bold ${
                        portfolio.summary.total_gain_loss >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {portfolio.summary.total_gain_loss >= 0 ? '+' : ''}
                        TZS {formatCurrency(Math.abs(portfolio.summary.total_gain_loss))}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">
                        {t('Return %', 'Faida %')}
                      </p>
                      <p className={`text-xl font-bold ${
                        portfolio.summary.total_gain_loss_percent >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {portfolio.summary.total_gain_loss_percent >= 0 ? '+' : ''}
                        {portfolio.summary.total_gain_loss_percent.toFixed(2)}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Portfolio Performance Chart */}
                <PortfolioPerformanceChart 
                  currentValue={portfolio.summary.total_current_value}
                  totalGainLoss={portfolio.summary.total_gain_loss}
                  totalGainLossPercent={portfolio.summary.total_gain_loss_percent}
                />

                {/* Holdings List */}
                <Card className="border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center justify-between">
                      <span>{t('Holdings', 'Milki')} ({portfolio.items.length})</span>
                      <div className="flex gap-2">
                        {portfolio.summary?.stock_count > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <BarChart3 className="w-3 h-3 mr-1" />
                            {portfolio.summary.stock_count} {t('Stocks', 'Hisa')}
                          </Badge>
                        )}
                        {portfolio.summary?.utt_count > 0 && (
                          <Badge variant="outline" className="text-xs text-teal-600 border-teal-300">
                            <PieChart className="w-3 h-3 mr-1" />
                            {portfolio.summary.utt_count} {t('UTT Funds', 'Mifuko UTT')}
                          </Badge>
                        )}
                      </div>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {portfolio.items.map((item, i) => (
                        <div 
                          key={item.id}
                          className={`flex items-center justify-between p-4 rounded-xl transition-colors ${
                            item.asset_type === 'utt' 
                              ? 'bg-teal-50/50 dark:bg-teal-900/10 hover:bg-teal-100/50 dark:hover:bg-teal-900/20' 
                              : 'bg-muted/50 hover:bg-muted'
                          }`}
                          data-testid={`portfolio-item-${item.symbol}`}
                        >
                          <div 
                            className="flex items-center gap-4 flex-1 cursor-pointer"
                            onClick={() => item.asset_type === 'utt' ? navigate('/utt-amis') : item.symbol === 'FAIDA' ? navigate('/faida') : navigate(`/stocks/${item.symbol}`)}
                          >
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              item.asset_type === 'utt' 
                                ? 'bg-teal-500/10' 
                                : 'bg-primary/10'
                            }`}>
                              {item.asset_type === 'utt' ? (
                                <PieChart className="w-5 h-5 text-teal-600" />
                              ) : (
                                <span className="font-bold text-primary text-sm">{item.symbol}</span>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-foreground">{item.stock_name}</p>
                                {item.asset_type === 'utt' && (
                                  <Badge className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0">
                                    UTT
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm text-muted-foreground">
                                {item.quantity.toLocaleString()} {['utt', 'cis'].includes(item.asset_type) ? t('units', 'vitengo') : t('shares', 'hisa')} @ TZS {item.purchase_price.toLocaleString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="text-right">
                              <p className="font-semibold text-foreground">
                                TZS {formatCurrency(item.current_value)}
                              </p>
                              <p className={`text-sm font-medium ${
                                item.gain_loss >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {item.gain_loss >= 0 ? '+' : ''}
                                {item.gain_loss_percent.toFixed(2)}%
                              </p>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-primary"
                                onClick={(e) => handleEditClick(e, item)}
                                data-testid={`edit-portfolio-${item.symbol}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                onClick={(e) => handleDeleteClick(e, item)}
                                data-testid={`delete-portfolio-${item.symbol}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* View Projections Button */}
                <Button 
                  variant="outline"
                  className="w-full rounded-full"
                  onClick={() => navigate('/portfolio/projection')}
                  data-testid="view-projections-btn"
                >
                  <TrendingUp className="w-4 h-4 mr-2" />
                  {t('View Future Projections', 'Tazama Makadirio ya Baadaye')}
                </Button>
              </>
            ) : (
              <Card className="border-dashed">
                <CardContent className="p-12 text-center">
                  <Wallet className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {t('No Investments Yet', 'Bado Hakuna Uwekezaji')}
                  </h3>
                  <p className="text-muted-foreground mb-2">
                    {t('Start tracking your investments by adding DSE stocks or UTT AMIS funds', 'Anza kufuatilia uwekezaji wako kwa kuongeza hisa za DSE au mifuko ya UTT AMIS')}
                  </p>
                  <p className="text-sm text-primary mb-6">
                    {t('⚡ Add your first investment and see your portfolio score instantly!', '⚡ Ongeza uwekezaji wako wa kwanza na uone alama ya portfolio yako mara moja!')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button 
                      onClick={() => navigate('/portfolio/add')}
                      className="rounded-full"
                      data-testid="empty-add-stock-btn"
                    >
                      <BarChart3 className="w-4 h-4 mr-2" />
                      {t('Add DSE Stock', 'Ongeza Hisa za DSE')}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => navigate('/portfolio/add?type=utt')}
                      className="rounded-full border-teal-300 text-teal-700 hover:bg-teal-50"
                      data-testid="empty-add-utt-btn"
                    >
                      <PieChart className="w-4 h-4 mr-2" />
                      {t('Add UTT Fund', 'Ongeza Mfuko UTT')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Market Tab */}
          <TabsContent value="market" className="space-y-6 mt-6">
            {/* Market Indices */}
            <div className="grid sm:grid-cols-2 gap-4">
              {marketIndex && Object.entries(marketIndex).map(([key, index]) => (
                <Card key={key} className="border-border/50">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm text-muted-foreground">{index.name}</p>
                      <span className={`text-sm font-medium ${
                        index.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {index.ytd_change >= 0 ? '+' : ''}{index.ytd_change.toFixed(2)}%
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">
                      {index.current_value.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {t('52W Range:', 'Kiwango cha 52W:')} {index.year_low.toLocaleString()} - {index.year_high.toLocaleString()}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Asset Type Sub-Tabs */}
            <Tabs value={marketSubTab} onValueChange={setMarketSubTab}>
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="stocks" data-testid="market-tab-stocks">
                  {t('Stocks', 'Hisa')} ({stocks.length})
                </TabsTrigger>
                <TabsTrigger value="utt" data-testid="market-tab-utt">
                  UTT ({uttFunds.length})
                </TabsTrigger>
                <TabsTrigger value="etfs" data-testid="market-tab-etfs">
                  ETFs ({etfs.length})
                </TabsTrigger>
                <TabsTrigger value="bonds" data-testid="market-tab-bonds">
                  {t('Bonds', 'Bondi')} ({bonds.length})
                </TabsTrigger>
              </TabsList>

              {/* Stocks Sub-Tab */}
              <TabsContent value="stocks" className="mt-4">
                <Card className="border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {t('DSE Stocks', 'Hisa za DSE')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border text-left">
                            <th className="pb-3 text-sm font-medium text-muted-foreground">{t('Symbol', 'Nembo')}</th>
                            <th className="pb-3 text-sm font-medium text-muted-foreground">{t('Name', 'Jina')}</th>
                            <th className="pb-3 text-sm font-medium text-muted-foreground text-right">{t('Price', 'Bei')}</th>
                            <th className="pb-3 text-sm font-medium text-muted-foreground text-right">{t('YTD', 'YTD')}</th>
                            <th className="pb-3 text-sm font-medium text-muted-foreground text-right">{t('Market Cap', 'Thamani')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {stocks.map((stock) => (
                            <tr 
                              key={stock.symbol}
                              className="border-b border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => navigate(`/stocks/${stock.symbol}`)}
                              data-testid={`market-stock-${stock.symbol}`}
                            >
                              <td className="py-3">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-foreground">{stock.symbol}</span>
                                  {stock.cross_listed && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">
                                      X-Listed
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="py-3 text-sm text-muted-foreground max-w-[200px] truncate">
                                {language === 'sw' ? stock.name_sw : stock.name}
                              </td>
                              <td className="py-3 text-right font-medium text-foreground">
                                {stock.current_price.toLocaleString()}
                              </td>
                              <td className={`py-3 text-right font-medium ${
                                stock.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {stock.ytd_change >= 0 ? '+' : ''}{stock.ytd_change.toFixed(1)}%
                              </td>
                              <td className="py-3 text-right text-sm text-muted-foreground">
                                {formatCurrency(stock.market_cap)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* UTT Funds Sub-Tab */}
              <TabsContent value="utt" className="mt-4">
                <Card className="border-border/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {t('UTT AMIS Funds', 'Mifuko ya UTT AMIS')}
                      </CardTitle>
                      <Button variant="outline" size="sm" onClick={() => navigate('/utt-amis')}>
                        {t('Learn More', 'Jifunze Zaidi')}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {uttFunds.map((fund) => (
                        <div
                          key={fund.symbol}
                          className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/add-portfolio?type=utt&symbol=${fund.symbol}`)}
                          data-testid={`market-utt-${fund.symbol}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-foreground">
                                {language === 'sw' ? fund.name_sw : fund.name}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {fund.risk_level}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">{fund.category}</p>
                            {fund.min_investment && (
                              <p className="text-xs text-muted-foreground mt-1">
                                Min: TZS {fund.min_investment?.toLocaleString()}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-foreground">
                              TZS {fund.current_price?.toLocaleString()}
                            </p>
                            <p className={`text-sm font-medium ${
                              fund.annual_return >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              +{fund.annual_return}% {t('annual', 'kwa mwaka')}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* ETFs Sub-Tab */}
              <TabsContent value="etfs" className="mt-4">
                <Card className="border-border/50">
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">
                        {t('Exchange Traded Funds (ETFs)', 'Mifuko ya ETF')}
                      </CardTitle>
                      <Button variant="outline" size="sm" onClick={() => navigate('/etfs')}>
                        {t('Learn More', 'Jifunze Zaidi')}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-4">
                      {etfs.map((etf) => (
                        <div
                          key={etf.symbol}
                          className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                          onClick={() => navigate(`/add-portfolio?type=etf&symbol=${etf.symbol}`)}
                          data-testid={`market-etf-${etf.symbol}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-semibold text-foreground">{etf.symbol}</span>
                              <Badge variant="outline" className="text-xs">
                                {etf.risk_level}
                              </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {language === 'sw' ? etf.name_sw : etf.name}
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              {etf.category} • Min: TZS {etf.min_investment?.toLocaleString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-foreground">
                              TZS {etf.current_price?.toLocaleString()}
                            </p>
                            <p className={`text-sm font-medium ${
                              etf.ytd_change >= 0 ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {etf.ytd_change >= 0 ? '+' : ''}{etf.ytd_change}% YTD
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Bonds/T-Bills Sub-Tab */}
              <TabsContent value="bonds" className="mt-4">
                <Card className="border-border/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {t('Government Bonds & Treasury Bills', 'Hatifungani na Dhamana za Serikali')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-6">
                      {/* Treasury Bills */}
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Badge variant="secondary">T-Bills</Badge>
                          {t('Treasury Bills (Short-term)', 'Dhamana za Hazina (Muda Mfupi)')}
                        </h4>
                        <div className="grid gap-3">
                          {bonds.filter(b => b.symbol.startsWith('TBILL')).map((bill) => (
                            <div
                              key={bill.symbol}
                              className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => navigate(`/add-portfolio?type=bond&symbol=${bill.symbol}`)}
                              data-testid={`market-bond-${bill.symbol}`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-foreground">{bill.symbol}</span>
                                  <Badge variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                                    {bill.risk_level}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {language === 'sw' ? bill.name_sw : bill.name}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {t('Maturity', 'Ukomavu')}: {bill.maturity} • Min: TZS {bill.min_investment?.toLocaleString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-green-600">
                                  {bill.annual_return}%
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t('Current Yield', 'Faida ya Sasa')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Treasury Bonds */}
                      <div>
                        <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                          <Badge variant="secondary">Bonds</Badge>
                          {t('Treasury Bonds (Long-term)', 'Hatifungani za Hazina (Muda Mrefu)')}
                        </h4>
                        <div className="grid gap-3">
                          {bonds.filter(b => b.symbol.startsWith('TBOND')).map((bond) => (
                            <div
                              key={bond.symbol}
                              className="flex items-center justify-between p-4 rounded-lg border border-border/50 hover:bg-muted/50 cursor-pointer transition-colors"
                              onClick={() => navigate(`/add-portfolio?type=bond&symbol=${bond.symbol}`)}
                              data-testid={`market-bond-${bond.symbol}`}
                            >
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-semibold text-foreground">{bond.symbol}</span>
                                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                                    {bond.risk_level}
                                  </Badge>
                                </div>
                                <p className="text-sm text-muted-foreground">
                                  {language === 'sw' ? bond.name_sw : bond.name}
                                </p>
                                <p className="text-xs text-muted-foreground mt-1">
                                  {t('Maturity', 'Ukomavu')}: {bond.maturity} • Min: TZS {bond.min_investment?.toLocaleString()}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-2xl font-bold text-amber-600">
                                  {bond.annual_return}%
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t('Current Yield', 'Faida ya Sasa')}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Disclaimer for Market Data */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
              <Info className="w-3.5 h-3.5 flex-shrink-0" />
              <span>
                {t(
                  'Market data updated daily. For informational purposes only. Not financial advice. Products regulated by CMSA & BOT.',
                  'Data ya soko inasasishwa kila siku. Kwa madhumuni ya taarifa tu. Si ushauri wa kifedha. Bidhaa zinazodhibitiwa na CMSA & BOT.'
                )}
              </span>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Portfolio Item Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>
              {t('Edit Investment', 'Hariri Uwekezaji')}
            </DialogTitle>
            <DialogDescription>
              {editingItem && (
                <span>
                  {t('Update your', 'Sasisha')} {editingItem.stock_name} ({editingItem.symbol}) {t('holding', 'milki')}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-quantity">{t('Number of Shares', 'Idadi ya Hisa')}</Label>
              <Input
                id="edit-quantity"
                type="number"
                value={editForm.quantity}
                onChange={(e) => setEditForm({ ...editForm, quantity: e.target.value })}
                placeholder="e.g. 100"
                min="0"
                step="any"
                data-testid="edit-quantity-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-price">{t('Purchase Price (TZS)', 'Bei ya Ununuzi (TZS)')}</Label>
              <Input
                id="edit-price"
                type="number"
                value={editForm.purchase_price}
                onChange={(e) => setEditForm({ ...editForm, purchase_price: e.target.value })}
                placeholder="e.g. 1500"
                min="0"
                step="any"
                data-testid="edit-price-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-notes">{t('Notes (Optional)', 'Maelezo (Si lazima)')}</Label>
              <Input
                id="edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                placeholder={t('Any notes...', 'Maelezo yoyote...')}
                data-testid="edit-notes-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={isSubmitting}
            >
              {t('Cancel', 'Ghairi')}
            </Button>
            <Button
              onClick={handleEditSubmit}
              disabled={isSubmitting || !editForm.quantity || !editForm.purchase_price}
              data-testid="save-edit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Saving...', 'Inahifadhi...')}
                </>
              ) : (
                t('Save Changes', 'Hifadhi Mabadiliko')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('Remove Investment?', 'Ondoa Uwekezaji?')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deletingItem && (
                <span>
                  {t('Are you sure you want to remove', 'Una uhakika unataka kuondoa')} {deletingItem.quantity.toLocaleString()} {t('shares of', 'hisa za')} {deletingItem.stock_name} ({deletingItem.symbol})? {t('This action cannot be undone.', 'Kitendo hiki hakiwezi kutenduliwa.')}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSubmitting}>
              {t('Cancel', 'Ghairi')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isSubmitting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('Removing...', 'Inaondoa...')}
                </>
              ) : (
                t('Remove', 'Ondoa')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Floating Install Button for Mobile */}
      <FloatingInstallButton language={language} />
    </Layout>
  );
}
